import React from 'react'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { ChartConfig } from '@/services/types/api.types'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts'
import { useAppSelector } from '@/hooks/storeHooks/storehooks'
import { ExternalPageIcon } from '@/assets/icons'

type AreaChartPoint = {
  label?: string
  value: number
}

type AnalyticsCardProps = {
  title: string
  data: AreaChartPoint[]
  filterValue?: string
  filterOptions?: string[]
  onFilterChange?: (value: string) => void
  handleExternalPageIcon?: () => void
  height?: number
  lineColor?: string
  gradientFrom?: string
  gradientTo?: string
  className?: string
}

const AnalyticsCard: React.FC<AnalyticsCardProps> = ({
  title,
  data,
  filterValue = '7 days',
  filterOptions = ['7 days', '30 days', '90 days'],
  onFilterChange,
  handleExternalPageIcon,
  height = 200,
  lineColor = '#00B8A9',
  gradientFrom = 'rgba(255, 87, 34, 0.9)',
  gradientTo = 'rgba(255, 87, 34, 0.05)',
  className = '',
}) => {
  const chartConfig = {
    value: {
      label: title,
      color: lineColor,
    },
  } satisfies ChartConfig

  const gradientId = `areaGradient-${title.replace(/[^a-zA-Z0-9]/g, '-')}`

  const themeValue = useAppSelector(state => state.menu.value)
  const strokeColor =
    themeValue === 'dark' || themeValue === 'high-contrast-dark'
      ? 'white'
      : 'black'

  return (
    <div
      className={`bg-primary border border-border border-l-[4px] border-l-accent rounded-xl shadow-sm p-4 w-full ${className}`}
    >
      {/* Header */}
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-2'>
          <h3 className='text-kpi-heading-2 font-semibold text-primary-foreground'>
            {title}
          </h3>
          {handleExternalPageIcon && (
            <div onClick={handleExternalPageIcon}>
              <div className='text-primary-foreground transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]'>
                <ExternalPageIcon />
              </div>
            </div>
          )}
        </div>

        {filterOptions && filterOptions.length > 0 && (
          <select
            value={filterValue}
            onChange={e => onFilterChange?.(e.target.value)}
            className='text-[0.8125rem] border border-gray-300 rounded-md px-2 py-1 bg-gray-50 outline-none'
          >
            {filterOptions.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Chart */}
      <div className='relative w-full pt-[12px]'>
        <ChartContainer
          config={chartConfig}
          className='w-full'
          style={{ height }}
        >
          <AreaChart
            data={data}
            margin={{
              left: -20,
              right: 12,
              top: 20,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor={gradientFrom} />
                <stop offset='100%' stopColor={gradientTo} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey='label'
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              stroke={strokeColor}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              stroke={strokeColor}
              tickFormatter={value => Math.round(value).toString()}
              width={35}
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent className='bg-secondary' />}
            />
            <Area
              type='natural'
              dataKey='value'
              stroke={lineColor}
              strokeWidth={2}
              fillOpacity={1}
              fill={`url(#${gradientId})`}
            />
          </AreaChart>
        </ChartContainer>
      </div>
    </div>
  )
}

export default AnalyticsCard
